// Historical borders (aourednik/historical-basemaps, GPL-3.0), simplified into public/eras/<year>.json.
// Files never change in place; bump V if they are regenerated. Served with immutable long-cache headers (public/_headers).
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import YEARS from './content/era-years.json';

const V = 4;
const cache = new Map();
let NAMES = null;   // english polity name -> Hebrew (public/eras/names-he.json), loaded with the first era
const namesP = () => (NAMES ? Promise.resolve(NAMES) : fetch(`/eras/names-he.json?v=${V}`).then((r) => r.json()).then((n) => (NAMES = n)).catch(() => (NAMES = {})));
export const heName = (n) => (n && NAMES && NAMES[n]) || n || '';

// Some snapshots wind rings the "wrong" way for d3 (a polygon would cover the rest of the globe), and
// simplification collapses a few tiny rings into slivers that d3 reads as "the whole sphere". Fix per polygon:
// flip the winding if that gives a sensible area, otherwise drop the degenerate ring.
const HALF = 2 * Math.PI;
const fixPoly = (rings) => {
  if (d3.geoArea({ type: 'Polygon', coordinates: rings }) <= HALF) return rings;
  const flipped = rings.map((r) => [...r].reverse());
  return d3.geoArea({ type: 'Polygon', coordinates: flipped }) <= HALF ? flipped : null;
};
const fix = (f) => {
  const g = f.geometry; if (!g) return f;
  if (g.type === 'Polygon') { const c = fixPoly(g.coordinates); return c ? { ...f, geometry: { ...g, coordinates: c } } : { ...f, geometry: null }; }
  if (g.type === 'MultiPolygon') { const c = g.coordinates.map(fixPoly).filter(Boolean); return c.length ? { ...f, geometry: { ...g, coordinates: c } } : { ...f, geometry: null }; }
  return f;
};

// Fixes for anachronistic or mislabeled polities in the source data (20th century), by snapshot year.
const RENAMES = {
  1900: { 'Kingdom of Brazil': 'Brazil' },
  1930: { 'White Russia': 'USSR' },
  1938: { Israel: 'Mandatory Palestine (GB)' },
  1945: { Israel: 'Mandatory Palestine (GB)' },
};
for (const y of [1920, 1930, 1938, 1945]) (RENAMES[y] ||= {})['Tanzania, United Republic of'] = 'Tanganyika';
const rename = (y, f) => { const to = RENAMES[y]?.[f.properties.NAME]; return to ? { ...f, properties: { ...f.properties, NAME: to } } : f; };

/** The newest snapshot at or before `year` (the oldest one for anything earlier). */
export const snapshotFor = (year) => {
  if (year >= 1948 && year < 1960) return 1960;   // the 1945 map predates Israel, Korea's split, etc.
  let s = YEARS[0]; for (const y of YEARS) if (y <= year) s = y; return s;
};

/** -> Promise<{year, features}>; features carry properties.NAME (English polity name or null). */
export function loadEra(year) {
  const y = snapshotFor(year);
  if (!cache.has(y)) cache.set(y, Promise.all([namesP(), fetch(`/eras/${y}.json?v=${V}`).then((r) => r.json())]).then(([, t]) => ({
    year: y, features: topojson.feature(t, t.objects.era).features.map(fix).filter((f) => f.geometry).map((f) => rename(y, f)),
    borders: topojson.mesh(t, t.objects.era, (a, b) => a !== b),
  })).catch(() => { cache.delete(y); return null; }));
  return cache.get(y);
}

// Soft per-polity colours, stable by name.
const hue = (s) => { let h = 7; for (const c of s || '') h = (h * 31 + c.charCodeAt(0)) % 360; return h; };
export const polityFill = (name) => name ? `color-mix(in srgb, hsl(${hue(name)} 55% 55%) 38%, var(--land))` : 'var(--land)';

/** The named polity whose territory contains the point, or null. */
export const polityAt = (era, lon, lat) => { const f = era.features.find((f) => f.properties.NAME && d3.geoContains(f, [lon, lat])); return f ? f.properties.NAME : null; };
